import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Grid,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Payments as PaymentsIcon
} from "@mui/icons-material";
import SubscriptionProductCard from "../Subscription/SubscriptionProductCard";
import { useLocation, useNavigate } from "react-router-dom";
import { OrganizationDTO } from "../../types/Tenant";
import subscriptionService from "../../services/SubscriptionService";
import SubscriptionPlan, { CheckoutSessionRequest } from "../../types/Subscription";

interface OrganizationSubscriptionPanelProps {
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  organization: OrganizationDTO | null;
}

const OrganizationSubscriptionPanel: React.FC<OrganizationSubscriptionPanelProps> = ({
  expanded,
  onChange,
  organization
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [message, setMessage] = useState<{ text: string, type: 'success' | 'error' | 'info' } | null>(null);

  // Tab state for A La Carte vs Annual subscriptions
  const [subscriptionTab, setSubscriptionTab] = useState<number>(1); // 0 = A La Carte, 1 = Annual
  
  // State for the number of campaigns per year (2 or 4)
  const [campaignsPerYear, setCampaignsPerYear] = useState<number>(2); // Default to 2 campaigns

  // Subscription data states
  const [annualSubscriptions, setAnnualSubscriptions] = useState<SubscriptionPlan[]>([]);
  const [alaCarteSubscriptions, setAlaCarteSubscriptions] = useState<SubscriptionPlan[]>([]);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState<boolean>(true);

  // Check for Stripe checkout session completion
  useEffect(() => {
    const verifyCheckoutSession = async () => {
      // Get URL query parameters
      const queryParams = new URLSearchParams(location.search);
      const sessionId = queryParams.get('session_id');
      const canceled = queryParams.get('canceled');
      
      // Handle cancelation
      if (canceled === 'true') {
        setMessage({
          text: 'Payment was canceled. You can try again when you\'re ready.',
          type: 'info'
        });
        // Remove query params from URL without page refresh
        navigate(location.pathname, { replace: true });
        return;
      }
      
      // Verify completed session
      if (sessionId) {
        setIsProcessing(true);
        try {
          // Verify the session with our backend
          const result = await subscriptionService.verifyCheckoutSession(sessionId);
          
          if (result.success) {
            setMessage({
              text: 'Your subscription has been successfully updated!',
              type: 'success'
            });
            // Refresh organization data to get updated subscription tier
            // Note: You might need to implement a method to refresh organization data
            // or reload the page
          } else {
            setMessage({
              text: 'Payment verification failed. Please contact support.',
              type: 'error'
            });
          }
        } catch (error) {
          console.error('Error verifying checkout session:', error);
          setMessage({
            text: 'Failed to verify your payment. Please contact support.',
            type: 'error'
          });
        } finally {
          setIsProcessing(false);
          // Remove query params from URL without page refresh
          navigate(location.pathname, { replace: true });
        }
      }
    };
    
    verifyCheckoutSession();
  }, [location.search, location.pathname, navigate]);
  
  // Load subscription plans from API
  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      setIsLoadingSubscriptions(true);
      try {
        const [annualPlans, alaCartePlans] = await Promise.all([
          subscriptionService.getAnnualSubscriptions(),
          subscriptionService.getALaCarteSubscriptions()
        ]);

        // Add free community plan
        const freePlan: SubscriptionPlan = {
          subscription_id: 'free-community',
          subscription_stripe_id: 'free-community',
          subscription_name: 'Community',
          subscription_description: '',
          description: 'Free community plan with basic features to help you get started with mentoring programs.',
          subscription_price: 0,
          subscription_price_id: 'free',
          key_features: ['Single small campaign per year', 'Up to 25 applicants']
        };

        // Add free plan to annual subscriptions
        setAnnualSubscriptions([freePlan, ...annualPlans]);
        setAlaCarteSubscriptions(alaCartePlans);
      } catch (error) {
        console.error('Error loading subscription plans:', error);
        setMessage({
          text: 'Failed to load subscription plans. Please try again later.',
          type: 'error'
        });
      } finally {
        setIsLoadingSubscriptions(false);
      }
    };

    fetchSubscriptionPlans();
  }, []);

  // Handle subscription tab change (Annual vs A La Carte)
  const handleTabChange = (_event: React.MouseEvent<HTMLElement>, newValue: number) => {
    if (newValue !== null) {
      setSubscriptionTab(newValue);
    }
  };
  
  // Handle campaigns per year toggle change (2 or 4)
  const handleCampaignsPerYearChange = (_event: React.MouseEvent<HTMLElement>, newValue: number) => {
    if (newValue !== null) {
      setCampaignsPerYear(newValue);
    }
  };

  // Process subscription purchase
  const handleSubscription = async (planId: string) => {
    if (!organization?.id) {
      setMessage({
        text: "Organization information is not available",
        type: "error"
      });
      return;
    }

    if (organization.subscription_tier === planId) {
      setMessage({
        text: "You are already subscribed to this plan",
        type: "info"
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Build success and cancel URLs for Stripe checkout
      const baseUrl = window.location.origin;
      const currentPath = window.location.pathname;
      const successUrl = `${baseUrl}${currentPath}?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${baseUrl}${currentPath}?canceled=true`;

      // Create checkout session request object using the interface
      const checkoutSessionRequest: CheckoutSessionRequest = {
        organization_id: organization.id,
        plan_id: planId,
        success_url: successUrl,
        cancel_url: cancelUrl
      };
      
      // Determine if this is a subscription (annual) or one-time payment (a la carte)
      const isSubscription = subscriptionTab === 1; // 1 = Annual plans
      
      // Create checkout session using the request object
      const response = await subscriptionService.createCheckoutSession(
        checkoutSessionRequest.organization_id,
        checkoutSessionRequest.plan_id,
        checkoutSessionRequest.success_url,
        checkoutSessionRequest.cancel_url,
        isSubscription
      );

      // Redirect to Stripe checkout
      if (response.url) {
        window.location.href = response.url;
      } else {
        setMessage({
          text: "Failed to create checkout session",
          type: "error"
        });
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      setMessage({
        text: "Failed to create payment session. Please try again later.",
        type: "error"
      });
      setIsProcessing(false);
    }
  };

  const handleCloseMessage = () => setMessage(null);

  return (
    <Accordion 
      expanded={expanded} 
      onChange={onChange}
      sx={{ mb: 2, borderRadius: '8px', overflow: 'hidden' }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="subscription-panel-content"
        id="subscription-panel-header"
        sx={{ backgroundColor: '#f5f5f5' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PaymentsIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Subscription</Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: 'background.default',
            minHeight: '300px'
          }}
        >
          {/* Error/success messages */}
          {message && (
            <Snackbar
              open={true}
              autoHideDuration={6000}
              onClose={handleCloseMessage}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert
                onClose={handleCloseMessage}
                severity={message.type}
                sx={{ width: '100%' }}
              >
                {message.text}
              </Alert>
            </Snackbar>
          )}

          {/* Loading indicator */}
          {isLoadingSubscriptions && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Subscription plan display */}
          {!isLoadingSubscriptions && organization?.id && (
            <>
              {/* Subscription type toggle */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 4,
                gap: 2
              }}>
                <ToggleButtonGroup
                  color="primary"
                  value={subscriptionTab}
                  exclusive
                  onChange={handleTabChange}
                  aria-label="Subscription period"
                >
                  <ToggleButton value={1}>Annual Plans</ToggleButton>
                  <ToggleButton value={0}>A La Carte</ToggleButton>
                </ToggleButtonGroup>
                
                {/* Campaigns per year toggle - Only shown for Annual Plans */}
                {subscriptionTab === 1 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Campaigns Per Year
                    </Typography>
                    <ToggleButtonGroup
                      color="primary"
                      value={campaignsPerYear}
                      exclusive
                      onChange={handleCampaignsPerYearChange}
                      aria-label="Campaigns per year"
                      size="small"
                    >
                      <ToggleButton value={2}>2 Campaigns</ToggleButton>
                      <ToggleButton value={4}>4 Campaigns</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}
              </Box>

              {/* Plan cards display */}
              <Grid container spacing={3}>
                {/* Annual Plans */}
                {subscriptionTab === 1 && (
                  <>
                    {annualSubscriptions && annualSubscriptions.length > 0 ? (
                      // Filter plans based on campaigns per year (2 = Duo, 4 = Quad) and sort by price
                      annualSubscriptions
                        .filter(plan => {
                          // Always include the free community plan
                          if (plan.subscription_stripe_id === 'free-community') {
                            return true;
                          }
                          
                          // For other plans, filter by name containing 'Duo' or 'Quad'
                          const planNameLower = plan.subscription_name.toLowerCase();
                          if (campaignsPerYear === 2) {
                            return planNameLower.includes('duo');
                          } else {
                            return planNameLower.includes('quad');
                          }
                        })
                        // Sort by price (lowest to highest)
                        .sort((a, b) => {
                          // Free community plan always comes first
                          if (a.subscription_stripe_id === 'free-community') return -1;
                          if (b.subscription_stripe_id === 'free-community') return 1;
                          
                          // Get numeric value from price regardless of format
                          // If it's a string like "$349.00", extract the number
                          const getNumericPrice = (price: any): number => {
                            if (typeof price === 'number') {
                              return price;
                            } else if (typeof price === 'string') {
                              // Remove non-numeric chars except decimal point
                              const numStr = price.replace(/[^0-9.]/g, '');
                              return parseFloat(numStr) || 0;
                            }
                            return 0;
                          };
                          
                          const priceA = getNumericPrice(a.subscription_price);
                          const priceB = getNumericPrice(b.subscription_price);
                            
                          return priceA - priceB;
                        })
                        .map((plan) => {
                          // Check if this is the free community plan
                          const isFreeCommunitySub = plan.subscription_stripe_id === 'free-community';
                          // This makes the free Community plan always show as current
                          const isCurrentPlan = isFreeCommunitySub ? true : organization?.subscription_tier === plan.subscription_stripe_id;
                          const isPlanSelected = organization?.subscription_tier === plan.subscription_stripe_id;

                          return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={plan.subscription_stripe_id}>
                              <SubscriptionProductCard
                                plan={plan}
                                isCurrentPlan={isCurrentPlan}
                                isSelected={isPlanSelected}
                                onSelectPlan={() => handleSubscription(plan.subscription_stripe_id)}
                                isProcessing={isProcessing && organization?.subscription_tier === plan.subscription_stripe_id}
                                // Format the price display
                                isAnnualPlan={true}
                              />
                            </Grid>
                          );
                        })
                    ) : (
                      <Grid item xs={12}>
                        <Typography variant="body1" align="center">
                          No annual subscription plans are currently available.
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}

                {/* A La Carte Plans */}
                {subscriptionTab === 0 && (
                  <>
                    {alaCarteSubscriptions && alaCarteSubscriptions.length > 0 ? (
                      alaCarteSubscriptions.map((plan) => {
                        const isPlanSelected = organization.subscription_tier === plan.subscription_stripe_id;
                        const isCurrentPlan = organization.subscription_tier === plan.subscription_stripe_id;

                        return (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={plan.subscription_stripe_id}>
                            <SubscriptionProductCard
                              plan={plan}
                              isCurrentPlan={isCurrentPlan}
                              isSelected={isPlanSelected}
                              onSelectPlan={() => handleSubscription(plan.subscription_stripe_id)}
                              isProcessing={isProcessing && organization.subscription_tier === plan.subscription_stripe_id}
                            />
                          </Grid>
                        );
                      })
                    ) : (
                      <Grid item xs={12}>
                        <Typography variant="body1" align="center">
                          No a-la-carte subscription plans are currently available.
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </>
          )}

          {/* No organization message */}
          {!organization?.id && !isLoadingSubscriptions && (
            <Alert severity="info">
              Please create an organization first to manage subscription plans.
            </Alert>
          )}
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
};

export default OrganizationSubscriptionPanel;
